import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarCheck, Car, Leaf, LogOut } from "lucide-react";
import { useMsal } from "@azure/msal-react";
import { IvpLogo } from "@/components/IvpLogo";
import emas5Hero from "@/assets/emas5-hero.png";
import { AvailabilitySection } from "@/components/AvailabilitySection";
import { BookingForm } from "@/components/BookingForm";
import { ConfirmationPanel } from "@/components/ConfirmationPanel";
import { AllBookingsTab } from "@/components/AllBookingsTab";
import { fetchVehicles, fetchAvailability, createBooking } from "@/lib/api";
import { todayISO } from "@/lib/date";

const today = todayISO;

export default function BookingPage() {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(today());
  const [confirmed, setConfirmed] = useState(null);
  const [activeTab, setActiveTab] = useState("book");
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const { data: vehicles = [] } = useQuery({
    queryKey: ["vehicles"],
    queryFn: fetchVehicles,
  });

  const { data: availability = [], isLoading: loadingAvailability, isFetching: fetchingAvailability, refetch: refetchAvailability } = useQuery({
    queryKey: ["availability", selectedDate],
    queryFn: () => fetchAvailability(selectedDate),
    enabled: !!selectedDate,
  });

  const mutation = useMutation({
    mutationFn: createBooking,
    onSuccess: (data) => {
      setConfirmed(data);
      queryClient.invalidateQueries({ queryKey: ["availability"] });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Booking failed. Please try again.");
    },
  });

  const allBookedSlots = useMemo(
    () =>
      availability.reduce((acc, v) => {
        acc[v.vehicle] = v.booked_slots;
        return acc;
      }, {}),
    [availability],
  );

  const handleClose = () => {
    setConfirmed(null);
    setSelectedDate(today());
  };

  return (
    <div className="flex min-h-screen flex-col items-center bg-slate-100 px-4 py-8 sm:px-6">
      <div className={`mx-auto w-full ${activeTab === "all" ? "max-w-6xl" : "max-w-5xl"} space-y-5 pb-20`}>
        <div className="flex items-center justify-end gap-3 text-xs text-[#64748B]" data-testid="signed-in-bar">
          <span>Signed in as <span className="font-semibold text-[#0F172A]" data-testid="signed-in-email">{account?.name || account?.username}</span></span>
          <button
            onClick={() => instance.logoutPopup({ account })}
            className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 font-semibold text-[#0F172A] transition-all hover:bg-slate-50"
            data-testid="portal-signout"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
        <header className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="hero-section">
          <div className="absolute left-0 right-0 top-0 h-2 bg-[#5C2D91]" aria-hidden="true" />
          <div className="p-6 pt-8 sm:p-10 sm:pt-10">
            <IvpLogo />
            <div className="mt-6 grid items-center gap-8 lg:grid-cols-2">
              <div>
                <div className="flex items-start gap-3">
                  <Leaf className="mt-1 h-7 w-7 shrink-0 text-[#16a34a] sm:h-9 sm:w-9" strokeWidth={2.2} aria-hidden="true" />
                  <h1 className="font-heading text-2xl font-extrabold leading-[1.05] tracking-tight sm:text-3xl lg:text-4xl" data-testid="hero-title">
                    <span className="text-[#5C2D91]">IVP Fleet</span>
                    <br />
                    <span className="text-[#0F172A]">Management System</span>
                  </h1>
                </div>
                <p className="mt-4 pl-10 text-base font-medium text-[#64748B] sm:text-lg" data-testid="hero-subtitle">
                  Powered by IVP Tech
                </p>
                <div className="ml-10 mt-4 h-1 w-16 rounded-full bg-[#16a34a]" aria-hidden="true" />
                <div className="mt-6 flex flex-nowrap items-center gap-x-1.5 pl-0 text-[11px] font-semibold text-[#0F172A] sm:gap-x-4 sm:pl-10 sm:text-sm">
                  <span className="flex items-center gap-1 whitespace-nowrap sm:gap-2" data-testid="hero-feature-availability">
                    <CalendarCheck className="h-3.5 w-3.5 shrink-0 text-[#16a34a] sm:h-5 sm:w-5" aria-hidden="true" />
                    Check Availability
                  </span>
                  <span className="h-3.5 w-px shrink-0 bg-slate-200 sm:h-5" aria-hidden="true" />
                  <span className="flex items-center gap-1 whitespace-nowrap sm:gap-2" data-testid="hero-feature-book">
                    <Car className="h-3.5 w-3.5 shrink-0 text-[#16a34a] sm:h-5 sm:w-5" aria-hidden="true" />
                    Book your Ride
                  </span>
                  <span className="h-3.5 w-px shrink-0 bg-slate-200 sm:h-5" aria-hidden="true" />
                  <span className="flex items-center gap-1 whitespace-nowrap sm:gap-2" data-testid="hero-feature-green">
                    <Leaf className="h-3.5 w-3.5 shrink-0 text-[#16a34a] sm:h-5 sm:w-5" aria-hidden="true" />
                    Go Green
                  </span>
                </div>
              </div>
              <div className="order-first lg:order-last">
                <img
                  src={emas5Hero}
                  alt="Proton e.MAS 5 electric vehicle with IVP logo, charging"
                  className="h-auto w-full object-contain"
                  data-testid="hero-image"
                />
              </div>
            </div>
          </div>
        </header>

        <div className="flex gap-2 rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm" data-testid="page-tabs">
          <button
            onClick={() => setActiveTab("book")}
            className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all ${activeTab === "book" ? "bg-[#5C2D91] text-white" : "text-[#64748B] hover:bg-slate-50"}`}
            data-testid="tab-book"
          >
            New Booking
          </button>
          <button
            onClick={() => setActiveTab("all")}
            className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all ${activeTab === "all" ? "bg-[#5C2D91] text-white" : "text-[#64748B] hover:bg-slate-50"}`}
            data-testid="tab-all-bookings"
          >
            All Bookings
          </button>
        </div>

        {activeTab === "all" ? (
          <AllBookingsTab />
        ) : (
          <AnimatePresence mode="wait">
            {confirmed ? (
              <motion.div key="confirmation">
                <ConfirmationPanel booking={confirmed} onClose={handleClose} />
              </motion.div>
            ) : (
              <motion.div key="booking" className="space-y-6">
                <AvailabilitySection
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                  availability={availability}
                  isLoading={loadingAvailability || fetchingAvailability}
                  onRefresh={() => refetchAvailability()}
                />
                {selectedDate < today() ? (
                  <div className="mx-auto w-full max-w-2xl rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm" data-testid="past-date-readonly">
                    <p className="font-heading text-base font-semibold text-[#0F172A]">Viewing past bookings</p>
                    <p className="mt-1 text-sm text-[#64748B]">
                      This date has already passed, so it is read-only. Select today or a future date to make a new booking.
                    </p>
                  </div>
                ) : (
                  <div className="mx-auto w-full max-w-2xl">
                    <BookingForm
                      vehicles={vehicles}
                      bookingDate={selectedDate}
                      onSubmit={(payload) => mutation.mutate(payload)}
                      submitting={mutation.isPending}
                      allBookedSlots={allBookedSlots}
                      lockedName={account?.name || ""}
                      lockedEmail={(account?.username || "").toLowerCase()}
                    />
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        )}

        <p className="text-center text-xs text-[#94a3b8]">
          IVP Fleet · Internal use only
        </p>
      </div>
    </div>
  );
}
